import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import siteService from "@plannel/services/data/site-master-service";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const SiteMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });


  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.page : 1);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [tPages, setTotalPages] = useState(0);

  const [sortParams, setSortParams] = useState({});
  const searchCode = useRef(reduxViewState?.searchCode ?? "");
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [sites, setSites] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columnDefs = [
    {
      headerName: "siteCd",
      field: "siteCd",
      width: 160,
      cellClass: 'stringType',
      filterType: 'string'
    },
    { headerName: "siteName", field: "name", width: 140, filterType: 'string' },
    { headerName: "currencyCd", field: "currencyCd", width: 140, filterType: 'string' },
    { headerName: "descTxt", field: "descTxt", filterType: 'string' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (searchCode.current) {
      searchFilters.siteCd = searchCode.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrievesites = () => {
    const params = getRequestParams();
    siteService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setSites(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createViewState(viewName, searchCode.current, currentPage, pageSize);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const removesite = () => {
    const siteIds = [];
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        siteIds.push(data.id)
      }
    );

    if (siteIds.length > 0) {
      siteService.remove(siteIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrievesites();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const savesite = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      siteService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .then(() => {
          retrievesites();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      savesite()
    } else if (action === "DELETE") {
      removesite()
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrievesites();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrievesites();
    }

    return () => {
      setSites([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }

    return () => {
      setSites([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const addRow = () => {
    const newRow = {
      id: null,
      siteCd: null,
      name: null,
      currencyCd: null,
      descTxt: null,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  return (
    <>
      <FilterContainer>
        <TextFieldSearch sx={{ width: 250 }} label={"siteCd"} searchCode={searchCode} retrieve={initPageToRetrieve} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={siteService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => {
            gridRef.current?.api?.stopEditing();
            setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.site, 'delete')}` })
          }} />
          <SaveButton onClick={() => {
            gridRef.current?.api?.stopEditing();
            setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })
          }} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="site-master-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={sites}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(SiteMaster);